// Shared track / session / stage configuration for the Bible Study CRM and Teacher portal.
// Both CoordinatorDashboard.jsx and TeacherGuestDetail.jsx / TeacherDashboard.jsx
// consume from this single source of truth. Keep this file free of JSX so it stays lightweight.

import { Sparkles, Users, Church, Home, GraduationCap } from "lucide-react";

// ---------- Discipleship stages (used in filter pills, badges, etc.) ----------
export const STAGES = [
  { key: "new", label: "New", color: "var(--muted)" },
  { key: "monday_night", label: "Introduction", color: "var(--terracotta)" },
  { key: "church_study", label: "Church Study", color: "#60A5FA" },
  { key: "ready_for_assignment", label: "Needs teacher", color: "var(--ember)" },
  { key: "home_study", label: "Home Study", color: "var(--olive)" },
  { key: "first_steps", label: "Next Steps", color: "var(--gold)" },
  { key: "completed", label: "Completed", color: "var(--ink)" },
];
export const STAGE_META = Object.fromEntries(STAGES.map((s) => [s.key, s]));

// ---------- Tracks (each with count, icon, color, resource support) ----------
// `count` = number of lesson slots stored on the guest record.
// `requiredCount` (optional) = number needed for the section to count as complete.
// Defaults to `count` when omitted (i.e. "all required"). When present it means the
// section is either/or and only that many completions are required.
export const TRACKS = [
  { key: "initial_contact", label: "Guest Connection",       short: "Connection", count: 8, requiredCount: 3, icon: Sparkles,       color: "var(--ember)",     hasResources: true },
  { key: "monday",          label: "Introduction to the Gospel", short: "Introduction", count: 2, requiredCount: 1, icon: Users,       color: "var(--terracotta)", hasResources: true },
  { key: "church",          label: "Church Bible Study",       short: "Church",     count: 3,  icon: Church,         color: "#60A5FA",          hasResources: true },
  { key: "home",            label: "Home Bible Study",         short: "Home",       count: 7,  icon: Home,           color: "var(--olive)",     hasResources: true },
  { key: "first_steps",     label: "Next Steps",               short: "Next Steps", count: 24, icon: GraduationCap,  color: "var(--gold)",      hasResources: true },
];
export const TRACK_META = Object.fromEntries(TRACKS.map((t) => [t.key, t]));
export const trackRequired = (t) => t.requiredCount ?? t.count;

// Guest document field name for each track's session array.
export const TRACK_FIELD = {
  initial_contact: "initial_contact_items",
  monday: "monday_sessions",
  church: "church_lessons",
  home: "home_lessons",
  first_steps: "first_steps_lessons",
};

// ---------- Named sessions per track (falls back to "Session {n}") ----------
export const SESSION_TITLES = {
  initial_contact: [
    "Send Personal Welcome Video",
    "Personal Follow-Up Text",
    "Remember Something About Them",
    "Introduce Them to Someone",
    "Second-Visit Recognition",
    "Small Personal Gesture",
    "Personal Invitation",
    "Ask About a Prayer Need",
  ],
  monday: ["Into His Marvelous Light", "The Gospel"],
  church: ["The Death of Jesus", "The Burial of Jesus", "The Resurrection of Jesus"],
  home: [
    "God's Way vs. Man's Way",
    "Noah's Way",
    "The Exodus",
    "The Tabernacle",
    "The New Birth",
    "Jesus' Name",
    "The Spirit",
  ],
  first_steps: [
    "Introduction",
    "The Bible: Our Daily Bread",
    "The Godhead, Part 1",
    "The Godhead, Part 2",
    "God's Eternal Plan: Redemption",
    "Repentance",
    "Baptism in Jesus' Name",
    "Receiving the Holy Ghost",
    "The 8 Levels of Spiritual Growth, Part 1",
    "The 8 Levels of Spiritual Growth, Part 2",
    "Trials and Burdens",
    "Enemies of the Soul",
    "Prayer",
    "Fasting",
    "Spiritual Authority",
    "Finances",
    "Praise and Worship",
    "The Holiness Lifestyle",
    "Scriptural Dress",
    "Standards of Adornment",
    "Hair is a Symbol",
    "Entertainment",
    "Getting Involved",
    "Graduation",
  ],
};
export const sessionTitle = (trackKey, i) => SESSION_TITLES[trackKey]?.[i] || `Session ${i + 1}`;

// ---------- Longer helper text shown under each session title ----------
// Currently used by the "Guest Connection" checklist so coords/teachers
// see the "how to" for each step without leaving the guest profile.
export const SESSION_DESCRIPTIONS = {
  initial_contact: [
    "Within 24 hours, send a short video using their name, thanking them for visiting and personally inviting them back.",
    "A few days later, send a simple conversational message — not an automated church announcement. Something like, \u201CHey John! Just wanted to check in and say it was great having you Sunday. Hope you\u2019re having a good week!\u201D",
    "Record one small personal detail from your conversation: spouse/kids, job, hometown, hobby, prayer need, how they found the church, etc. This gives whoever follows up next something genuine to remember.",
    "On their next visit, intentionally connect them with another church member who has something in common: similar age, children, occupation, interests, season of life, etc.",
    "Make sure somebody is specifically looking for them when they return. \u201CJohn! Man, I\u2019m glad you came back!\u201D Being remembered is powerful.",
    "After their first or second visit, do something small when appropriate: coffee, handwritten card, church merchandise, a children\u2019s treat for their kids, etc. It doesn\u2019t need to cost much.",
    "Invite them to something other than the main service: dinner, small group, church fellowship, youth event, men\u2019s/women\u2019s event, etc. This starts moving the relationship beyond \u201Cchurch guest\u201D into \u201Csomeone we know.\u201D",
    "Not as a formality. Ask if there is anything you can genuinely pray with them about. Record it so someone can follow up later and ask, \u201CHow did that situation turn out?\u201D",
  ],
};
export const sessionDescription = (trackKey, i) => SESSION_DESCRIPTIONS[trackKey]?.[i] || "";

// ---------- Resource link kinds per track ----------
export const TRACK_RESOURCE_KINDS = {
  initial_contact: [{ key: "notes", label: "Teacher notes" }],
  monday:          [{ key: "notes", label: "Teacher notes" }],
  church:          [{ key: "notes", label: "Teacher notes" }],
  home:            [{ key: "notes", label: "Teacher notes" }],
  first_steps: [
    { key: "youtube",       label: "YouTube" },
    { key: "teacher_notes", label: "Teachers Notes" },
    { key: "slides",        label: "Slides" },
    { key: "student_sheet", label: "Student Sheet" },
  ],
};
// Next Steps Introduction (lesson 1) has its own set of 5 links.
export const NS_INTRO_RESOURCE_KINDS = [
  { key: "youtube",       label: "YouTube" },
  { key: "slides",        label: "Slides" },
  { key: "student_sheet", label: "Student Sheet" },
  { key: "class_roster",  label: "Class Roster" },
  { key: "binder_cover",  label: "Binder Cover" },
];
// Next Steps lessons 2–23 use this order, with a Classroom Poster.
export const NS_LESSON_RESOURCE_KINDS = [
  { key: "teacher_notes",    label: "Teacher's Notes" },
  { key: "slides",           label: "Slideshow" },
  { key: "student_sheet",    label: "Student Sheet" },
  { key: "classroom_poster", label: "Classroom Poster" },
  { key: "youtube",          label: "YouTube" },
];
export const resourceKindsFor = (trackKey, i) => {
  if (trackKey === "first_steps" && i === 0) return NS_INTRO_RESOURCE_KINDS;
  if (trackKey === "first_steps" && i >= 1 && i <= 22) return NS_LESSON_RESOURCE_KINDS;
  return TRACK_RESOURCE_KINDS[trackKey] || [];
};

// ---------- Small utilities ----------
export const AGE_GROUPS = ["Child", "Preteen", "Youth", "College & Career", "Young Married", "Married", "Singles", "Seasoned Saints", "Other"];

export const todayIso = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

export const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "—";

// Total lesson count across all tracks (used for overall guest progress rings)
export const TOTAL_LESSON_COUNT = TRACKS.reduce((sum, t) => sum + trackRequired(t), 0);
